import { z } from 'zod';

/**
 * Message Activity Log (BB-105, AN-011): why messages were not delivered — suppressions, caps, deferrals, aborts,
 * provider errors, invalidated tokens, unreachable recipients — per campaign, Canvas or globally, test sends included.
 * A read view over lifecycle facts (BB-132) and unreachable dispatches (BB-98); nothing is copied.
 */

/** How many days back the log shows (environment setting `activityLogRetentionDays`). */
export const ACTIVITY_LOG_RETENTION_DAYS = { min: 1, max: 90, default: 14 } as const;

/** Entry types: the fact type, or `unreachable` for a send that found no reachable channel at all. */
export const ACTIVITY_LOG_TYPES = [
  'suppressed',
  'capped',
  'deferred',
  'aborted',
  'failed',
  'provider_outcome_unknown',
  'token_invalidated',
  'unreachable',
  'card_skipped',
  'card_withdrawn',
  'iam_deferred',
  'iam_discarded',
  'iam_aborted',
] as const;
export type ActivityLogType = (typeof ACTIVITY_LOG_TYPES)[number];

export const ACTIVITY_LOG_CHANNELS = ['push', 'email', 'sms', 'content_card', 'in_app', 'webhook'] as const;
export type ActivityLogChannel = (typeof ACTIVITY_LOG_CHANNELS)[number];

export const ACTIVITY_LOG_PAGE = { default: 50, max: 200 } as const;

const Bool = z.enum(['true', 'false']).transform((v) => v === 'true');

/**
 * `GET /admin/v1/environments/:environmentId/activity-log` (`reports.view`). `reason` matches the code or its base
 * (`frequency_cap` finds `frequency_cap:<rule>`); `from` earlier than the retention window is moved up to it.
 */
export const ActivityLogQuery = z.object({
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
  campaignId: z.uuid().optional(),
  canvasId: z.uuid().optional(),
  canvasStepId: z.uuid().optional(),
  channel: z.enum(ACTIVITY_LOG_CHANNELS).optional(),
  type: z.enum(ACTIVITY_LOG_TYPES).optional(),
  reason: z.string().trim().min(1).max(255).optional(),
  profileId: z.uuid().optional(),
  isTest: Bool.optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(ACTIVITY_LOG_PAGE.max).default(ACTIVITY_LOG_PAGE.default),
});
export type ActivityLogQuery = z.infer<typeof ActivityLogQuery>;

export const REASON_CATEGORIES = ['consent', 'eligibility', 'delivery_rule', 'content', 'provider', 'device', 'card', 'in_app', 'other'] as const;
export type ReasonCategory = (typeof REASON_CATEGORIES)[number];

/** A reason code with its human description; `param` is what follows `:` (a cap rule id, an event name). */
export interface ReasonView {
  code: string;
  base: string;
  param: string | null;
  category: ReasonCategory;
  description: { en: string; uk: string };
}

export interface ActivityLogEntry {
  /** The fact id, or `<dispatchId>:<deliveryChannel>` for an unreachable send. */
  id: string;
  occurredAt: string;
  type: ActivityLogType;
  /** The pipeline's reason code as written (`quiet_hours`, `frequency_cap:<rule>`, `unregistered`, …). */
  reasonCode: string | null;
  reason: ReasonView | null;
  channel: ActivityLogChannel | null;
  platform: string | null;
  /** The app the push went to (from the push delivery); null for other channels. */
  appInstanceId: string | null;
  source: 'campaign' | 'canvas' | 'test';
  campaign: { id: string; name: string | null } | null;
  canvas: { id: string; name: string | null; stepId: string | null } | null;
  variantApiId: string | null;
  /** The dispatch trace (`delivery_dispatch`), when the entry belongs to one. */
  dispatchId: string | null;
  profileId: string;
  isTest: boolean;
  /** Safe extra context only (stage, error class, provider, card id, delivery channel); never tokens or profile data. */
  detail: Record<string, string | number | boolean>;
}

export interface ActivityLogPage {
  entries: ActivityLogEntry[];
  nextCursor: string | null;
  /** The window actually read. */
  from: string;
  to: string;
  retentionDays: number;
}
